import { useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { Modal } from 'react-bootstrap';
import Api from '../../Auth/Api';
import ModalUnidade from './ModalUnidade';
import DropdownMenu from '../DropdownMenu';

const UnidadesManager = ({ token, onToast }) => {
    const [dataUnidades, setDataUnidades] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingSave, setLoadingSave] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [unidadeEditando, setUnidadeEditando] = useState(null);
    const [showModalDelete, setShowModalDelete] = useState(false);
    const [unidadeParaDeletar, setUnidadeParaDeletar] = useState(null);
    const [loadingDelete, setLoadingDelete] = useState(false);
    const [search, setSearch] = useState('');
    const [refreshKey, setRefreshKey] = useState(0);

    const onToastRef = useRef(onToast);
    onToastRef.current = onToast;

    const { register, handleSubmit, reset, formState: { errors } } = useForm();

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            setLoading(true);
            try {
                const response = await Api.get('api/listar-unidades', {
                    headers: { Authorization: `Bearer ${token}` }
                });
                if (!cancelled) setDataUnidades(response.data || []);
            } catch (error) {
                if (!cancelled) {
                    setDataUnidades([]);
                    if (error.response?.status !== 400) {
                        onToastRef.current?.('Erro!', error.response?.data?.message || 'Não foi possível carregar as unidades.', 'danger');
                    }
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        load();
        return () => { cancelled = true; };
    }, [token, refreshKey]);

    const handleOpenCreate = () => {
        setUnidadeEditando(null);
        reset({ nome: '' });
        setShowModal(true);
    };

    const handleOpenEdit = (unidade) => {
        setUnidadeEditando(unidade);
        reset({ nome: unidade.nome });
        setShowModal(true);
    };

    const handleOpenDelete = (unidade) => {
        setUnidadeParaDeletar(unidade);
        setShowModalDelete(true);
    };

    const onSubmit = async (data) => {
        setLoadingSave(true);
        try {
            if (unidadeEditando) {
                await Api.post(`api/editar-unidade/${unidadeEditando.id}`, data, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                onToast?.('Sucesso!', 'Unidade atualizada com sucesso.', 'success');
            } else {
                await Api.post('api/nova-unidade', data, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                onToast?.('Sucesso!', 'Unidade criada com sucesso.', 'success');
            }
            setShowModal(false);
            reset({ nome: '' });
            setRefreshKey((prev) => prev + 1);
        } catch (error) {
            onToast?.('Erro!', error.response?.data?.message || 'Erro ao salvar unidade.', 'danger');
        } finally {
            setLoadingSave(false);
        }
    };

    const handleDelete = async () => {
        if (!unidadeParaDeletar) return;
        setLoadingDelete(true);
        try {
            await Api.delete(`api/deletar-unidade/${unidadeParaDeletar.id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            onToast?.('Sucesso!', 'Unidade excluída com sucesso.', 'success');
            setShowModalDelete(false);
            setUnidadeParaDeletar(null);
            setRefreshKey((prev) => prev + 1);
        } catch (error) {
            onToast?.('Erro!', error.response?.data?.message || 'Erro ao excluir unidade.', 'danger');
            setShowModalDelete(false);
        } finally {
            setLoadingDelete(false);
        }
    };

    const unidadesFiltradas = dataUnidades.filter((u) =>
        !search || u.nome.toLowerCase().includes(search.toLowerCase())
    );

    return (
        <>
            <div className="d-flex justify-content-between align-items-center mb-4 mt-4">
                <div>
                    <h5 className="mb-1 fw-semibold text-primary">Gerenciar Unidades</h5>
                    <p className="text-muted mb-0 fs-7">Cadastre as unidades disponíveis para atribuição aos usuários.</p>
                </div>
                <button type="button" className="btn btn-primary" onClick={handleOpenCreate}>
                    <i className="bi bi-plus-lg me-2"></i>
                    Nova unidade
                </button>
            </div>

            <div className="card border-0 shadow-sm mb-3">
                <div className="card-body p-3">
                    <div className="inner-addon right-addon">
                        <i className="d-flex bi bi-search" />
                        <input
                            type="search"
                            className="form-control py-2"
                            placeholder="Pesquise pelo nome da unidade..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>
                </div>
            </div>

            {loading ? (
                <div className="d-flex justify-content-center my-5">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Carregando...</span>
                    </div>
                </div>
            ) : (
                <div className="table-responsive">
                    <table className="table table-hover align-middle">
                        <thead className="table-light">
                            <tr>
                                <th>Nome</th>
                                <th className="text-end">Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            {unidadesFiltradas.length === 0 ? (
                                <tr>
                                    <td colSpan="2" className="text-center text-muted py-4">
                                        {search ? 'Nenhuma unidade encontrada para a pesquisa.' : 'Nenhuma unidade cadastrada.'}
                                    </td>
                                </tr>
                            ) : (
                                unidadesFiltradas.map((unidade) => (
                                    <tr key={unidade.id}>
                                        <td className="fw-medium">{unidade.nome}</td>
                                        <td className="text-end">
                                            <div style={{ position: 'static' }} className="d-flex align-items-center justify-content-end">
                                                <DropdownMenu
                                                    items={[
                                                        {
                                                            icon: 'pencil',
                                                            label: 'Editar unidade',
                                                            onClick: () => handleOpenEdit(unidade)
                                                        },
                                                        { divider: true },
                                                        {
                                                            icon: 'trash3-fill',
                                                            label: 'Excluir unidade',
                                                            onClick: () => handleOpenDelete(unidade)
                                                        }
                                                    ]}
                                                />
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                        {dataUnidades.length > 0 && (
                            <tfoot>
                                <tr>
                                    <th colSpan="2" className="bg-primary text-white rounded-bottom">
                                        <div className="table-footer d-flex justify-content-between align-items-center flex-wrap gap-1">
                                            <span>{unidadesFiltradas.length} de {dataUnidades.length} unidade(s)</span>
                                        </div>
                                    </th>
                                </tr>
                            </tfoot>
                        )}
                    </table>
                </div>
            )}

            <ModalUnidade
                show={showModal}
                onHide={() => setShowModal(false)}
                unidadeEditando={unidadeEditando}
                register={register}
                errors={errors}
                handleSubmit={handleSubmit}
                onSubmit={onSubmit}
                loading={loadingSave}
            />

            <Modal show={showModalDelete} onHide={() => setShowModalDelete(false)} backdrop="static" centered>
                <Modal.Header closeButton>
                    <span className="fw-semibold text-danger">Excluir unidade</span>
                </Modal.Header>
                <Modal.Body>
                    Tem certeza que deseja excluir a unidade <strong>{unidadeParaDeletar?.nome}</strong>?
                </Modal.Body>
                <Modal.Footer>
                    <button type="button" className="btn" onClick={() => setShowModalDelete(false)} disabled={loadingDelete}>
                        Cancelar
                    </button>
                    <button type="button" className="btn btn-danger" onClick={handleDelete} disabled={loadingDelete}>
                        {loadingDelete ? 'Excluindo...' : 'Excluir'}
                    </button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default UnidadesManager;
